import type { Abgangsleitung, AnyNetworkElement } from '../types/network';

/**
 * Speaking display names for network elements (UI-Audit C2).
 *
 * Imports frequently deliver cables named after their internal id
 * ("Line_48d215da13f3", "Kabel_…") — meaningless to an operator. Where the
 * name is such an auto id, the display name is built from what the operator
 * recognises: the cable type (real delivered type from metadata.Type, else
 * the model's cableTypeString) and the two endpoints. The raw id stays
 * available for tooltips.
 */

/** True when a name is just an id in disguise (Line_ab12cd, kle0173…, uuid, …). */
export function isAutoGeneratedName(name: string | null | undefined, id?: string): boolean {
  if (!name) return true;
  const n = name.trim();
  if (id && n === id) return true;
  if (/^(line|kabel|cable|kle|seg|segment)[_ -]?[0-9a-f]{6,}$/i.test(n)) return true;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(n)) return true;
  if (/^(has|kle|sta|kvs|muf)[0-9a-f]{8,}$/i.test(n)) return true;
  return false;
}

/** Short, human name of any element (houses: address or "Anschluss …", others: name). */
export function shortElementName(el: AnyNetworkElement | undefined | null): string | null {
  if (!el) return null;
  if (el.type === 'hausanschluss') {
    const ha = el as { street?: string | null; houseNumber?: string | null; name?: string; id: string };
    const addr = [ha.street, ha.houseNumber].filter(Boolean).join(' ').trim();
    if (addr) return addr;
    if (ha.name && !isAutoGeneratedName(ha.name, ha.id)) return ha.name;
    return `Anschluss …${ha.id.slice(-6)}`;
  }
  if (el.type === 'muffe') return 'Muffe';
  if (el.name && !isAutoGeneratedName(el.name, el.id)) return el.name;
  const typeLabel = el.type === 'trafostation' ? 'Trafostation' : el.type === 'kabelverteilschrank' ? 'Verteiler' : 'Element';
  return `${typeLabel} …${el.id.slice(-6)}`;
}

/** Cable type as delivered (metadata.Type) with the model type as fallback. */
export function cableTypeLabel(cable: Pick<Abgangsleitung, 'metadata' | 'cableTypeString' | 'crossSection'>): string {
  const delivered = cable.metadata?.['Type'] ?? cable.metadata?.['type'];
  if (typeof delivered === 'string' && delivered.trim()) return delivered.trim();
  if (cable.cableTypeString) return cable.cableTypeString;
  return cable.crossSection ? `${cable.crossSection} mm²` : 'Kabel';
}

/**
 * Display name of a cable. Keeps a real name; replaces an auto id by
 * "<Typ> · <von> → <nach>" (endpoints via the resolver) or "<Typ> · …<id>".
 */
export function cableDisplayName(
  cable: Abgangsleitung,
  resolve?: (id: string) => AnyNetworkElement | undefined,
): string {
  if (!isAutoGeneratedName(cable.name, cable.id)) return cable.name;
  const type = cableTypeLabel(cable);
  const from = cable.sourceId && resolve ? shortElementName(resolve(cable.sourceId)) : null;
  const to = cable.targetId && resolve ? shortElementName(resolve(cable.targetId)) : null;
  if (from && to) return `${type} · ${from} → ${to}`;
  if (from || to) return `${type} · ${from ?? to}`;
  return `${type} · …${cable.id.slice(-6)}`;
}

/** Display name for any element type (cables get the speaking cable name). */
export function elementDisplayName(
  el: AnyNetworkElement,
  resolve?: (id: string) => AnyNetworkElement | undefined,
): string {
  if (el.type === 'abgangsleitung') return cableDisplayName(el as Abgangsleitung, resolve);
  return shortElementName(el) ?? el.id;
}
